import { AdjustmentsHorizontalIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { createSelector } from '@reduxjs/toolkit';
import debug from 'debug';
import React, { useCallback, useMemo } from 'react';
import { Constants } from '../../../../Constants';
import { useChat } from '../../../../libs/hooks';
import { useAuthoritive } from '../../../../libs/hooks/useAuthoritive';
import { useBucket } from '../../../../libs/hooks/useBucket';
import { useAppDispatch, useAppSelector } from '../../../../redux/app/hooks';
import { RootState } from '../../../../redux/app/store';
import { setBuckets, updateBucket } from '../../../../redux/features/buckets/BucketsSlice';
import { BucketItem } from '../../../../redux/features/buckets/BucketsState';
import {
    editConversationScope,
    hideIntelligentFilteringMessage,
} from '../../../../redux/features/conversations/conversationsSlice';
import { ShineBorder } from '../../../Effects/ShineBorder';

const log = debug(Constants.debug.root).extend('intelligent-filtering-message');

const selectIntelligentFilteringData = createSelector(
    [
        (state: RootState) => state.conversations.selectedId,
        (state: RootState) => state.conversations.conversations,
        (state: RootState) => state.buckets,
        (state: RootState) => state.app.activeUserInfo?.email ?? '',
    ],
    (selectedId, conversations, buckets, userEmail) => ({
        selectedId,
        conversations,
        buckets,
        userEmail,
    }),
);

export const IntelligentFilteringMessage = () => {
    const dispatch = useAppDispatch();
    const { selectedId, conversations, buckets, userEmail } = useAppSelector(selectIntelligentFilteringData);
    const { Buckets, AuthDocsScope } = buckets;
    const chat = useChat();
    const authoritive = useAuthoritive();
    const bucket = useBucket();
    const currentConversation = useMemo(() => conversations[selectedId], [conversations, selectedId]);
    const targetBucketId = currentConversation?.intelligentFilteringBucketId;

    const suggestedBucket: BucketItem | undefined = useMemo(() => {
        if (!targetBucketId) return undefined;
        return Buckets.find((b) => b.id === targetBucketId);
    }, [Buckets, targetBucketId]);

    const loadBucketsIfNeeded = useCallback(() => {
        if (targetBucketId && Buckets.length === 0) {
            bucket.getBuckets(userEmail).catch((err) => {
                log('error getting buckets', err);
            });
        }
    }, [targetBucketId, bucket, userEmail, Buckets.length]);

    React.useEffect(() => {
        loadBucketsIfNeeded();
    }, [loadBucketsIfNeeded]);

    const handleDismiss = useCallback(() => {
        dispatch(hideIntelligentFilteringMessage({ chatId: selectedId }));
    }, [dispatch, selectedId]);

    const handleChangeScope = useCallback(async () => {
        if (!suggestedBucket || !currentConversation) return;

        dispatch(hideIntelligentFilteringMessage({ chatId: selectedId }));

        try {
            const authoritiveDocuments = Buckets.filter((b) => b.parent?.id === suggestedBucket.id).map((b) => b.id);

            const authScopeName = await authoritive.getAuthDocsScopeName(userEmail);

            if (!authScopeName) {
                console.error('No AuthDocs scope found');
                return;
            }

            await chat.editChat(
                currentConversation.id,
                currentConversation.title,
                currentConversation.memoryBalance,
                authScopeName,
                currentConversation.selectedSites,
                currentConversation.hidden,
                authoritiveDocuments,
            );

            if (AuthDocsScope) {
                localStorage.setItem('lastSelectedChatScope', `${AuthDocsScope}`);
            }

            dispatch(setBuckets(Buckets.map((b) => ({ ...b, selected: false }))));
            dispatch(updateBucket({ ...suggestedBucket, selected: true }));
            dispatch(editConversationScope({ id: selectedId, newScope: authScopeName }));
        } catch (error) {
            const errorMessage = `Unable to change chat scope. Details: ${
                error instanceof Error ? error.message : String(error)
            }`;
            console.error(errorMessage);
        }
    }, [
        suggestedBucket,
        currentConversation,
        dispatch,
        selectedId,
        Buckets,
        authoritive,
        userEmail,
        chat,
        AuthDocsScope,
    ]);

    if (!suggestedBucket) {
        return null;
    }

    return (
        <div className="flex justify-center bg-slate-100 p-2 bg-slate-200 leading-6 shadow-md dark:bg-slate-900">
            <ShineBorder className="w-full mx-4 relative" color={'#E44EE9'} borderWidth={2}>
                <div className="flex flex-col items-start justify-center w-full h-full p-6 text-gray-500 bg-slate-50 dark:bg-slate-800 dark:text-slate-300 rounded-lg">
                    <button
                        className="absolute top-2 right-2 p-2 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-gray-400 hover:text-primary-500"
                        title="Dismiss"
                        aria-label="Dismiss"
                        onClick={handleDismiss}>
                        <XMarkIcon className="h-6 w-6" />
                    </button>
                    <p className="text-2xl font-bold mb-3">Intelligent Filtering</p>
                    <p className="text-base mb-4">
                        For best accuracy, authoritative '{suggestedBucket.title}' documents were used to answer your
                        question. Click 'Keep Scope' if you intend to ask similar follow-up questions.
                    </p>
                    <button
                        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-300 hover:bg-primary-700 text-slate-50 transition-colors"
                        title="Keep scope"
                        aria-label="Keep scope"
                        onClick={handleChangeScope}>
                        <AdjustmentsHorizontalIcon className="h-5 w-5" />
                        <span className="text-sm font-medium">Keep scope</span>
                    </button>
                </div>
            </ShineBorder>
        </div>
    );
};
